import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SupportChatPanel from "../components/SupportChatPanel";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ShieldCheck, Mail, MessageSquare, Phone, MessagesSquare, ArrowRight, ExternalLink } from "lucide-react";
import SEOHead from "../components/SEOHead";

import { useTranslation } from 'react-i18next';
const SUPPORT_EMAIL = "support@maxemployee.com";
const SUPPORT_WHATSAPP_NUMBER = "15551234567"; // replace with the real support number
const SUPPORT_TELEGRAM_URL = "https://t.me/share/url";
const SUPPORT_DISCORD_URL = "https://discord.gg/maxemployeesupport"; // replace with the real support entry point

const CHANNELS = [
  { label: "Email", icon: Mail, note: "Opens a prefilled email to the support team." },
  { label: "Telegram", icon: MessageSquare, note: "Opens Telegram with a prefilled support message." },
  { label: "WhatsApp", icon: Phone, note: "Opens WhatsApp with your name and email included." },
  { label: "Discord", icon: MessagesSquare, note: "Opens the Discord support entry point." },
];

function buildSupportMessage(user, channelLabel) {
  const customerName = user?.name || user?.full_name || user?.email || "there";
  const customerEmail = user?.email || "";

  return [
    "Hi Support,",
    "",
    `My name is ${customerName}.`,
    customerEmail ? `My email is ${customerEmail}.` : null,
    `I need help via ${channelLabel} for my Max OS Cloud support request.`,
    "",
    "Issue:",
    "",
  ]
    .filter(Boolean)
    .join("\n");
}

export default function Support() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [actionNote, setActionNote] = useState("");

  const supportLinks = useMemo(() => {
    const message = buildSupportMessage(user, "support");
    const encodedMessage = encodeURIComponent(message);
    const encodedSupportUrl = encodeURIComponent(`${window.location.origin}/support`);

    return {
      email: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Max OS Cloud Support")}&body=${encodedMessage}`,
      telegram: `${SUPPORT_TELEGRAM_URL}?url=${encodedSupportUrl}&text=${encodedMessage}`,
      whatsapp: `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodedMessage}`,
      discord: SUPPORT_DISCORD_URL,
      supportMessage: message,
    };
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/support", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/support/access");
        if (cancelled) return;
        setHasAccess(Boolean(data?.has_access));
        if (!data?.has_access) {
          navigate("/dashboard?support=locked", { replace: true });
        }
      } catch {
        if (!cancelled) navigate("/dashboard", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, navigate, user]);

  const openChannel = async (channel) => {
    setActionNote("");

    try {
      if (channel === "Email") {
        setActionNote("Opening your email client with a prefilled support message.");
        window.location.href = supportLinks.email;
        return;
      }

      if (channel === "Telegram") {
        setActionNote("Opening Telegram with a prefilled support message.");
        window.open(`${supportLinks.telegram}`, "_blank", "noopener,noreferrer");
        return;
      }

      if (channel === "WhatsApp") {
        setActionNote("Opening WhatsApp with your customer details included.");
        window.open(supportLinks.whatsapp, "_blank", "noopener,noreferrer");
        return;
      }

      if (channel === "Discord") {
        await navigator.clipboard.writeText(supportLinks.supportMessage);
        setActionNote("Copied your support message. Opening Discord now.");
        window.open(supportLinks.discord, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      setActionNote(error?.message || "Unable to open the support channel right now.");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('support.support_center_max_employee')}
        description={t('support.max_employee_support_center_get_help_with_your_ai')}
        url="/support"
        noIndex
      />
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> Loading support center...
        </div>
        <Footer />
      </div>
    );
  }

  if (!hasAccess) return null;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />

      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// SUPPORT CENTER</div>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.95]">
              Live support for <span className="text-amber-500">{t('support.max_os_cloud')}</span>.
            </h1>
            <p className="mt-5 max-w-2xl text-zinc-300 leading-relaxed">
              Use the quick contact cards to open Email, Telegram, WhatsApp, or Discord directly. The chat below stays as the main support thread.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
              >
                Back to dashboard
              </Link>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-800 bg-zinc-950/40 text-zinc-300 font-mono text-[10px] uppercase tracking-[0.2em]">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Entitlement checked
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 border border-emerald-500/20 bg-emerald-500/5 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.2em]">
                <ExternalLink className="w-3.5 h-3.5" /> Quick contact ready
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CHANNELS.map((channel) => {
              const Icon = channel.icon;
              return (
                <button
                  key={channel.label}
                  type="button"
                  onClick={() => openChannel(channel.label)}
                  className="border border-zinc-800 bg-zinc-950/60 p-4 flex flex-col gap-3 text-left hover:border-amber-500/60 hover:bg-amber-500/5 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-amber-500" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300">{channel.label}</div>
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed flex-1">{channel.note}</p>
                  <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                    Open {channel.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        {actionNote && (
          <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-4">
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-200">{actionNote}</div>
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        <SupportChatPanel productId="max-os-cloud" compact={false} />
      </div>

      <Footer />
    </div>
  );
}
